import React, {
  useEffect,
  useRef,
  forwardRef,
  useImperativeHandle,
} from 'react'
import * as echarts from 'echarts'
import { safeChartInit } from '../../../../utils/echarts'
import type { TFunction } from 'i18next'
import type { TaxRateYearlyData } from '@popety-io/types'

interface TaxRateEvolutionChartProps {
  data: TaxRateYearlyData[]
  height?: number
  t: TFunction
}

export interface TaxRateEvolutionChartRef {
  exportChart: () => string | null
}

export const TaxRateEvolutionChart = forwardRef<
  TaxRateEvolutionChartRef,
  TaxRateEvolutionChartProps
>(({ data, height = 350, t }, ref) => {
  const chartRef = useRef<HTMLDivElement>(null)
  const chartInstance = useRef<echarts.ECharts | null>(null)

  useImperativeHandle(ref, () => ({
    exportChart: () => {
      if (chartInstance.current) {
        return chartInstance.current.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#fff',
        })
      }
      return null
    },
  }))

  useEffect(() => {
    if (!chartRef.current || !data || data.length === 0) return

    chartInstance.current = safeChartInit(chartRef.current)

    const sortedData = [...data].sort((a, b) => a.year - b.year)

    const years = sortedData.map((d) => d.year.toString())
    const cantonData = sortedData.map((d) => d.income_rate_canton)
    const cityData = sortedData.map((d) => d.income_rate_city)

    const series: echarts.SeriesOption[] = [
      {
        name: t('taxRates.cantonRate'),
        type: 'line',
        data: cantonData,
        lineStyle: { color: '#4a90d9', width: 2.5 },
        itemStyle: { color: '#4a90d9' },
        symbol: 'circle',
        symbolSize: 5,
        smooth: false,
      },
      {
        name: t('taxRates.cityRate'),
        type: 'line',
        data: cityData,
        lineStyle: { color: '#6bb86b', width: 2.5 },
        itemStyle: { color: '#6bb86b' },
        symbol: 'circle',
        symbolSize: 5,
        smooth: false,
      },
    ]

    const legendData = series.map((s) => s.name as string)

    const option: echarts.EChartsOption = {
      tooltip: {
        trigger: 'axis',
        formatter: (params: any) => {
          const year = params[0].axisValue
          let content = `<strong>${year}</strong><br/>`
          params.forEach((p: any) => {
            content += `<span style="color:${p.color}">●</span> ${p.seriesName}: ${p.value}%<br/>`
          })
          return content
        },
      },
      legend: {
        data: legendData,
        top: 10,
        textStyle: { fontSize: 12 },
      },
      grid: {
        left: '3%',
        right: '4%',
        bottom: '3%',
        top: '15%',
        containLabel: true,
      },
      xAxis: {
        type: 'category',
        data: years,
        axisLabel: { fontSize: 11, color: '#1f2937' },
        axisLine: { lineStyle: { color: '#e5e7eb' } },
      },
      yAxis: {
        type: 'value',
        name: t('taxRates.rateAxis'),
        axisLabel: {
          fontSize: 11,
          color: '#1f2937',
          formatter: '{value}%',
        },
        axisLine: { show: false },
        axisTick: { show: false },
        splitLine: { lineStyle: { color: '#f3f4f6', type: 'dashed' } },
      },
      series,
    }

    chartInstance.current.setOption(option)

    const handleResize = () => chartInstance.current?.resize()
    window.addEventListener('resize', handleResize)

    return () => {
      window.removeEventListener('resize', handleResize)
      chartInstance.current?.dispose()
    }
  }, [data, height, t])

  if (!data || data.length === 0) {
    return (
      <div
        style={{
          height: `${height}px`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: '#6b7280',
        }}
      >
        {t('charts.noData')}
      </div>
    )
  }

  // Calculate insights
  const sortedData = [...data].sort((a, b) => a.year - b.year)
  const firstYear = sortedData[0]
  const lastYear = sortedData[sortedData.length - 1]

  const cantonChange =
    lastYear.income_rate_canton - firstYear.income_rate_canton
  const cityChange = lastYear.income_rate_city - firstYear.income_rate_city

  return (
    <div>
      <div
        ref={chartRef}
        style={{ width: '100%', height: `${height}px`, minHeight: '300px' }}
      />

      {/* Trend insights */}
      <div
        style={{
          marginTop: '16px',
          padding: '12px 16px',
          backgroundColor: '#f8fafc',
          borderRadius: '8px',
          border: '1px solid #e2e8f0',
        }}
      >
        <div
          style={{
            fontSize: '14px',
            fontWeight: '600',
            color: '#1f2937',
            marginBottom: '8px',
          }}
        >
          {t('charts.taxRateEvolution.summary.title', {
            startYear: firstYear.year,
            endYear: lastYear.year,
          })}
        </div>
        <div style={{ fontSize: '13px', color: '#4b5563', lineHeight: '1.4' }}>
          • <strong>{t('taxRates.cantonRate')}:</strong>{' '}
          {firstYear.income_rate_canton}% → {lastYear.income_rate_canton}%
          {cantonChange !== 0 && (
            <span style={{ color: cantonChange < 0 ? '#10b981' : '#ef4444' }}>
              {' '}
              ({cantonChange > 0 ? '+' : ''}
              {cantonChange.toFixed(1)})
            </span>
          )}
          <br />• <strong>{t('taxRates.cityRate')}:</strong>{' '}
          {firstYear.income_rate_city}% → {lastYear.income_rate_city}%
          {cityChange !== 0 && (
            <span style={{ color: cityChange < 0 ? '#10b981' : '#ef4444' }}>
              {' '}
              ({cityChange > 0 ? '+' : ''}
              {cityChange.toFixed(1)})
            </span>
          )}
        </div>
      </div>
    </div>
  )
})

TaxRateEvolutionChart.displayName = 'TaxRateEvolutionChart'
